/**
 * Barcode drawing for labels: EAN-13 for 13-digit codes with a right check digit (what shops and
 * scanners know best), Code 128 for everything else. Returns the bars as widths in modules,
 * starting with a bar; the caller draws them.
 */

const L = ['0001101', '0011001', '0010011', '0111101', '0100011', '0110001', '0101111', '0111011', '0110111', '0001011'];
const R = L.map((p) => [...p].map((b) => (b === '0' ? '1' : '0')).join(''));
const G = R.map((p) => [...p].reverse().join(''));
const PARITY = ['LLLLLL', 'LLGLGG', 'LLGGLG', 'LLGGGL', 'LGLLGG', 'LGGLLG', 'LGGGLL', 'LGLGLG', 'LGLGGL', 'LGGLGL'];

// Code 128 widths (bar, space, bar, space, bar, space) for values 0–106; 106 is the stop, with its final bar.
const C128 = (
  '212222 222122 222221 121223 121322 131222 122213 122312 132212 221213 221312 231212 112232 122132 122231 113222 123122 123221 223211 221132 ' +
  '221231 213212 223112 312131 311222 321122 321221 312212 322112 322211 212123 212321 232121 111323 131123 131321 112313 132113 132311 211313 ' +
  '231113 231311 112133 112331 132131 113123 113321 133121 313121 211331 231131 213113 213311 213131 311123 311321 331121 312113 312311 332111 ' +
  '314111 221411 431111 111224 111422 121124 121421 141122 141221 112214 112412 122114 122411 142112 142211 241211 221114 413111 241112 134111 ' +
  '111242 121142 121241 114212 124112 124211 411212 421112 421211 212141 214121 412121 111143 111341 131141 114113 114311 411113 411311 113141 ' +
  '114131 311141 411131 211412 211214 211232 2331112'
).split(' ');

export function gs1Check(body: string): number {
  let sum = 0;
  for (let i = 0; i < body.length; i++) sum += Number(body[body.length - 1 - i]) * (i % 2 === 0 ? 3 : 1);
  return (10 - (sum % 10)) % 10;
}
export const isEan13 = (code: string) => /^\d{13}$/.test(code) && gs1Check(code.slice(0, 12)) === Number(code[12]);

/** A string of 0 and 1 modules into run widths, starting with a bar. */
function runs(bits: string): number[] {
  const out: number[] = [];
  let i = 0;
  while (i < bits.length) {
    let j = i;
    while (j < bits.length && bits[j] === bits[i]) j++;
    out.push(j - i);
    i = j;
  }
  return out;
}

function ean13(code: string): number[] {
  const parity = PARITY[Number(code[0])]!;
  let bits = '101';
  for (let i = 1; i <= 6; i++) bits += (parity[i - 1] === 'L' ? L : G)[Number(code[i])];
  bits += '01010';
  for (let i = 7; i <= 12; i++) bits += R[Number(code[i])];
  bits += '101';
  return runs(bits);
}

/** Code 128, set B (printable ASCII). */
function code128(text: string): number[] {
  const values = [104, ...[...text].map((c) => c.charCodeAt(0) - 32)];
  const check = values.reduce((sum, v, i) => sum + v * (i === 0 ? 1 : i), 0) % 103;
  return [...values, check, 106].flatMap((v) => [...C128[v]!].map(Number));
}

export interface Bars {
  kind: 'ean13' | 'code128';
  widths: number[];
  /** Total width in modules, without the quiet zones. */
  modules: number;
}
export function encode(code: string): Bars {
  const widths = isEan13(code) ? ean13(code) : code128(code);
  return { kind: isEan13(code) ? 'ean13' : 'code128', widths, modules: widths.reduce((a, b) => a + b, 0) };
}
